import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { Theme } from "../types";
import { themeService } from "../services/themeService";
import { usePreferences } from "./PreferencesContext";
import debugx from "debugx";

const debug = debugx("bikedash:theme-context");

export interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: "light" | "dark";
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { userPreferences } = usePreferences();
  const [theme, setTheme] = useState<Theme>(userPreferences.theme || "auto");
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("dark");

  // Apply theme when user preferences change
  useEffect(() => {
    const newTheme = userPreferences.theme || "auto";
    debug("User preference theme changed to: %s", newTheme);
    setTheme(newTheme);
    themeService.applyTheme(newTheme);
  }, [userPreferences.theme]);

  // Subscribe to theme service updates (for auto mode)
  useEffect(() => {
    const unsubscribe = themeService.subscribe(
      (currentTheme, currentResolved) => {
        debug(
          "Theme service update: theme=%s, resolved=%s",
          currentTheme,
          currentResolved,
        );
        setTheme(currentTheme);
        setResolvedTheme(currentResolved);
      },
    );

    return unsubscribe;
  }, []);

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
